import { useState } from "react";
import { UserName } from "@/components/badges/UserName";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, BookOpen } from "lucide-react";
import { useMentorTopics } from "@/hooks/useMentorTopics";
import { useCreateMentorshipRequest } from "@/hooks/useMentorshipRequests";
import { useStorageUrl } from "@/hooks/useStorageUrl";
import { useFoundationsEligibility } from "@/hooks/useFoundationsEligibility";
import { FoundationsRequiredBanner } from "@/components/mentorship/FoundationsRequiredBanner";
import { toast } from "sonner";

interface MentorshipRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mentor: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
}

export function MentorshipRequestDialog({
  open,
  onOpenChange,
  mentor,
}: MentorshipRequestDialogProps) {
  const [selectedTopicId, setSelectedTopicId] = useState<string>("");
  const [message, setMessage] = useState("");

  const { data: topics, isLoading: topicsLoading } = useMentorTopics();
  const createRequest = useCreateMentorshipRequest();
  const mentorAvatarUrl = useStorageUrl("avatars", mentor.avatar_url);
  
  // Check Foundations course eligibility
  const {
    canRequestMentorship,
    isEnrolled,
    progress,
    courseLink,
    isLoading: eligibilityLoading,
    isMentor,
  } = useFoundationsEligibility();

  const handleSubmit = async () => {
    if (!selectedTopicId || !message.trim()) return;

    // Double-check eligibility before submitting
    if (!canRequestMentorship && !isMentor) {
      toast.error("Please complete the Foundations course before requesting mentorship.");
      return;
    }

    try {
      await createRequest.mutateAsync({
        mentorId: mentor.id,
        topicId: selectedTopicId,
        message: message.trim(),
      });
      setSelectedTopicId("");
      setMessage("");
      onOpenChange(false);
    } catch (error: any) {
      // Check if the error is due to RLS policy (course not completed)
      if (error?.message?.includes("row-level security") || error?.code === "42501") {
        toast.error("Please complete the Foundations course before requesting mentorship.");
      }
      // Other errors handled in mutation
    }
  };

  const handleClose = () => {
    setSelectedTopicId("");
    setMessage("");
    onOpenChange(false);
  };

  // Show loading state while checking eligibility
  if (eligibilityLoading) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Show Foundations requirement if not eligible (entrepreneurs only)
  if (!canRequestMentorship && !isMentor) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={mentorAvatarUrl || undefined} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {mentor.full_name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div>
                <span>Request Mentorship</span>
                <p className="text-sm font-normal text-muted-foreground">
                  from <UserName userId={mentor.id} name={mentor.full_name} />
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <FoundationsRequiredBanner
              isEnrolled={isEnrolled}
              progress={progress}
              courseLink={courseLink}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={mentorAvatarUrl || undefined} />
              <AvatarFallback className="bg-primary text-primary-foreground">
                {mentor.full_name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div>
              <span>Request Mentorship</span>
              <p className="text-sm font-normal text-muted-foreground">
                from <UserName userId={mentor.id} name={mentor.full_name} />
              </p>
            </div>
          </DialogTitle>
          <DialogDescription className="text-left pt-2">
            Choose the topic that best describes the help you're seeking. Mentors
            review requests in their own time and are never required to respond.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="topic">Topic *</Label>
            {topicsLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading topics...
              </div>
            ) : (
              <Select value={selectedTopicId} onValueChange={setSelectedTopicId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a topic" />
                </SelectTrigger>
                <SelectContent>
                  {topics?.map((topic) => (
                    <SelectItem key={topic.id} value={topic.id}>
                      <div className="flex items-center gap-2">
                        <BookOpen className="h-4 w-4 text-muted-foreground" />
                        {topic.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Your Message *</Label>
            <Textarea
              id="message"
              placeholder="Briefly describe what you're hoping to learn or discuss..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="min-h-[100px]"
            />
            <p className="text-xs text-muted-foreground">
              Be specific about your goals. This helps the mentor decide if they can help.
            </p>
          </div>
        </div>

        <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
          <p>
            <strong>What happens next?</strong> The mentor will see your request
            and can accept, decline, or simply not respond. If accepted, a
            mentorship conversation will open.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedTopicId || !message.trim() || createRequest.isPending}
          >
            {createRequest.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              "Send Request"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
